/**
 * Bloco "Seus dados" da página pública do formulário (2026-09-29) — aparece
 * quando o formulário pede os dados de contato (`settings.contactData`).
 * Nome, telefone (DDI + máscara BR, a mesma da captura por QR) e e-mail, cada
 * um obrigatório conforme o formulário. O backend confere de novo e acha o
 * contato pelo telefone (ou cria).
 */
import React from 'react';
import type { FormContactDataSettings, FormSubmissionContact } from '@troia-v3/types';
import { isPhoneComplete, maskBrPhone, phoneToE164Input } from '@/modules/crm/capture/public/phoneMask';

export interface PublicContactValues {
  name: string;
  ddi: string;
  phone: string;
  email: string;
}

export const EMPTY_PUBLIC_CONTACT: PublicContactValues = { name: '', ddi: '55', phone: '', email: '' };

/** Erros por campo (`contact.name`, `contact.phone`, `contact.email`) — as mesmas chaves do backend. */
export const validatePublicContact = (rule: FormContactDataSettings, v: PublicContactValues): Record<string, string> => {
  const errors: Record<string, string> = {};
  const phoneDigits = v.phone.replace(/\D/g, '');
  const email = v.email.trim();
  if (rule.requireName && v.name.trim().length < 2) errors['contact.name'] = 'Informe seu nome';
  if (phoneDigits !== '' && !isPhoneComplete(v.ddi, v.phone)) errors['contact.phone'] = 'Telefone inválido';
  else if (rule.requirePhone && phoneDigits === '') errors['contact.phone'] = 'Informe seu telefone';
  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors['contact.email'] = 'E-mail inválido';
  else if (rule.requireEmail && email === '') errors['contact.email'] = 'Informe seu e-mail';
  if (phoneDigits === '' && email === '' && !errors['contact.phone'] && !errors['contact.email']) {
    errors['contact.phone'] = 'Informe seu telefone ou e-mail';
  }
  return errors;
};

/** O que vai no `contact` do envio — só o que foi preenchido. */
export const buildContactPayload = (v: PublicContactValues): FormSubmissionContact => {
  const name = v.name.trim();
  const email = v.email.trim();
  return {
    ...(name !== '' ? { name } : {}),
    ...(v.phone.replace(/\D/g, '') !== '' ? { phone: phoneToE164Input(v.ddi, v.phone) } : {}),
    ...(email !== '' ? { email } : {}),
  };
};

interface PublicContactFieldsProps {
  rule: FormContactDataSettings;
  value: PublicContactValues;
  onChange: (value: PublicContactValues) => void;
  errors: Record<string, string>;
  primaryColor: string;
}

const inputClass = 'w-full min-h-[46px] border border-gray-300 rounded-[10px] px-[14px] py-3 text-[15px] focus:outline-none';

export const PublicContactFields: React.FC<PublicContactFieldsProps> = ({ rule, value, onChange, errors, primaryColor }) => {
  const set = (partial: Partial<PublicContactValues>) => onChange({ ...value, ...partial });
  const focus = (e: React.FocusEvent<HTMLElement>) => { e.currentTarget.style.borderColor = primaryColor; e.currentTarget.style.boxShadow = `0 0 0 3px ${primaryColor}1a`; };
  const blur = (e: React.FocusEvent<HTMLElement>) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = 'none'; };
  const obrigatorio = <span className="text-red-500 ml-1">*</span>;

  return (
    <div className="space-y-5 pb-6 border-b border-gray-200" data-testid="form-public-contact">
      <h2 className="text-base font-semibold text-gray-900">Seus dados</h2>
      <div>
        <label htmlFor="form-contact-name" className="block text-[13px] font-semibold text-gray-800 mb-[7px]">
          Nome{rule.requireName && obrigatorio}
        </label>
        <input
          id="form-contact-name"
          value={value.name}
          onChange={(e) => set({ name: e.target.value })}
          autoComplete="name"
          className={inputClass}
          onFocus={focus}
          onBlur={blur}
          data-testid="form-public-contact-name"
        />
        {errors['contact.name'] && <p className="text-xs text-red-500 mt-1" data-testid="form-public-contact-error-name">{errors['contact.name']}</p>}
      </div>
      <div>
        <label htmlFor="form-contact-phone" className="block text-[13px] font-semibold text-gray-800 mb-[7px]">
          Telefone{rule.requirePhone && obrigatorio}
        </label>
        <div className="flex items-center gap-2 border border-gray-300 rounded-[10px] px-[14px] min-h-[46px]">
          <span className="flex items-center gap-0.5 font-semibold text-sm text-gray-500 pr-2 border-r border-gray-300">
            +<input
              aria-label="DDI"
              value={value.ddi}
              onChange={(e) => set({ ddi: e.target.value.replace(/\D/g, '').slice(0, 3) })}
              inputMode="numeric"
              className="bg-transparent outline-none text-gray-500"
              style={{ width: 28 }}
              data-testid="form-public-contact-ddi"
            />
          </span>
          <input
            id="form-contact-phone"
            className="flex-1 bg-transparent outline-none text-[15px] tabular-nums"
            value={value.ddi === '55' ? maskBrPhone(value.phone) : value.phone.replace(/\D/g, '')}
            onChange={(e) => set({ phone: e.target.value })}
            inputMode="tel"
            autoComplete="tel-national"
            placeholder={value.ddi === '55' ? '(47) 99999-0000' : 'número'}
            data-testid="form-public-contact-phone"
          />
        </div>
        {errors['contact.phone'] && <p className="text-xs text-red-500 mt-1" data-testid="form-public-contact-error-phone">{errors['contact.phone']}</p>}
      </div>
      <div>
        <label htmlFor="form-contact-email" className="block text-[13px] font-semibold text-gray-800 mb-[7px]">
          E-mail{rule.requireEmail && obrigatorio}
        </label>
        <input
          id="form-contact-email"
          type="email"
          value={value.email}
          onChange={(e) => set({ email: e.target.value })}
          inputMode="email"
          autoComplete="email"
          placeholder="voce@email.com"
          className={inputClass}
          onFocus={focus}
          onBlur={blur}
          data-testid="form-public-contact-email"
        />
        {errors['contact.email'] && <p className="text-xs text-red-500 mt-1" data-testid="form-public-contact-error-email">{errors['contact.email']}</p>}
      </div>
    </div>
  );
};
